import {
  type PermissionContext,
  permissionsOperations,
  presentationPermissionOperations,
  resourcePermissionsOperations,
} from '@ardoq/access-control';
import { distinctUntilChanged, map, scan } from 'rxjs/operators';
import { shareReplay } from 'rxjs';
import { isEqual, keyBy, mapValues, pickBy } from 'lodash';
import { persistentWorkspaceOverviews$ as workspaces$ } from 'streams/workspaces/workspaceOverviews$';
import {
  persistentWorkspaces$,
  type WorkspacesState,
} from 'streams/workspaces/workspaces$';
import { assetFolders$ } from 'streams/assetFolders/assetFolders$';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import type { OrgUsersStreamShape } from 'streams/orgUsers/types';
import { presentations$ } from 'streams/presentations/presentations$';
import { type ClientAssetMeta, enhanceWithMeta } from './utils';
import {
  type APIBroadcastAttributes,
  type APIBookmarkAttributes,
  type APICurrentUserFavorites,
  type APIDashboardAttributes,
  type APIDiscoverViewpointAttributes,
  type APIPresentationAssetAttributes,
  type APIReportAttributes,
  type APIScenarioAttributes,
  type APISurveyAttributes,
  type APIViewpointAttributes,
  type APIWorkspaceOverview,
  ArdoqId,
  type AssetFolder,
  AssetType,
  type Meta,
  type PersistedMetamodel,
  APICurrentUser,
  type WorkspaceAsset,
} from '@ardoq/api-types';
import {
  dishonestCurrentUser$ as currentUser$,
  getFavorites,
} from 'streams/currentUser/currentUser$';
import { toByIdDictionary } from '@ardoq/common-helpers';
import { userOperations } from '@ardoq/core';
import { scenarioAccessControlInterface } from 'resourcePermissions/accessControlHelpers/scenarioAccessControlInterface';
import { derivedStream } from '@ardoq/rxbeach';
import { surveys$, type SurveysById } from 'streams/surveys/surveys$';
import { metamodels$ } from 'streams/metamodels/metamodels$';
import { reports$ } from 'streams/reports/reports$';
import { dashboards$ } from 'streams/dashboards/dashboards$';
import { scenarios$ } from 'streams/scenarios/scenarios$';
import { FeatureContext, Features, hasFeature } from '@ardoq/features';
import {
  Permissions,
  hasResourcePermission,
} from 'streams/currentUserPermissions/permissionInterface';
import { traversals$ } from 'streams/traversals$';
import { surveyAccessControlInterface } from 'resourcePermissions/accessControlHelpers/surveys';
import { broadcastAccessControlInterface } from 'resourcePermissions/accessControlHelpers/broadcasts';
import { featureContext$ } from 'streams/featureContext$';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { viewpoints$ } from '../../viewpoints/viewpoints$';
import { viewpointAccessControlInterface } from '../../resourcePermissions/accessControlHelpers/viewpoints';
import { broadcast$ } from '../../broadcasts/broadcast$';
import {
  getLatestSchedule,
  getIntervalInDays,
} from 'broadcasts/broadcastOverview/utils';
import { dashboardAccessControlInterface } from 'resourcePermissions/accessControlHelpers/dashboards';
import { traversalStateOperations } from 'streams/traversals/traversalsStateOperations';
import { bookmarks$ } from 'streams/bookmarks/bookmarks$';
import {
  dashboardHasInaccessibleConfidentialFields,
  reportHasInaccessibleConfidentialFields,
  workspaceFieldAccessibility$,
  WorkspaceFieldAccessibilityMap,
} from 'streams/fields/workspaceFieldAccessibility$';

export type AssetWithMeta<T, E> = T & Meta<E>;

type CurrentUserAssetData = {
  isOrgWriter: boolean;
  isOrgAdmin: boolean;
  favorites: APICurrentUserFavorites;
};

export type AssetsState = {
  assetFoldersById: {
    [id: string]: AssetWithMeta<AssetFolder, AssetType.FOLDER>;
  };
  workspaceAssetsById: {
    [id: string]: WorkspaceAsset;
  };
  presentationsById: {
    [id: string]: AssetWithMeta<
      APIPresentationAssetAttributes,
      AssetType.PRESENTATION
    >;
  };
  surveysById: {
    [id: string]: AssetWithMeta<APISurveyAttributes, AssetType.SURVEY>;
  };
  metamodelsById: {
    [id: string]: AssetWithMeta<PersistedMetamodel, AssetType.METAMODEL>;
  };
  scenariosById: {
    [id: string]: AssetWithMeta<APIScenarioAttributes, AssetType.SCENARIO>;
  };
  reportsById: {
    [id: string]: AssetWithMeta<APIReportAttributes, AssetType.REPORT>;
  };
  dashboardsById: {
    [id: string]: AssetWithMeta<APIDashboardAttributes, AssetType.DASHBOARD>;
  };
  traversalsById: {
    [id: string]: AssetWithMeta<APIViewpointAttributes, AssetType.TRAVERSAL>;
  };
  bookmarksById: {
    [id: string]: AssetWithMeta<APIBookmarkAttributes, AssetType.BOOKMARK>;
  };
  viewpointsById: {
    [id: string]: AssetWithMeta<
      APIDiscoverViewpointAttributes,
      AssetType.VIEWPOINT
    >;
  };
  broadcastsById: {
    [id: string]: AssetWithMeta<APIBroadcastAttributes, AssetType.BROADCAST>;
  };
  permissionContext: PermissionContext;
};
type InputStreamShape = [
  CurrentUserAssetData,
  Record<ArdoqId, AssetFolder>,
  Record<ArdoqId, APIWorkspaceOverview>,
  Record<ArdoqId, APIPresentationAssetAttributes>,
  SurveysById,
  Record<ArdoqId, PersistedMetamodel>,
  Record<ArdoqId, APIScenarioAttributes>,
  Record<ArdoqId, APIReportAttributes>,
  Record<ArdoqId, APIDashboardAttributes>,
  Record<ArdoqId, APIViewpointAttributes>,
  PermissionContext,
  Record<ArdoqId, APIBookmarkAttributes>,
  Record<ArdoqId, APIDiscoverViewpointAttributes>,
  Record<ArdoqId, APIBroadcastAttributes>,
  WorkspaceFieldAccessibilityMap,
  FeatureContext,
  ArdoqId[],
  Record<ArdoqId, string | undefined>,
];
export type AssetsStreamState = AssetsState & {
  previousInput: null | InputStreamShape;
  assetIdToFolderIdLookup: Record<string, ArdoqId>;
};
export const emptyState: AssetsStreamState = {
  assetFoldersById: {},
  workspaceAssetsById: {},
  presentationsById: {},
  surveysById: {},
  metamodelsById: {},
  scenariosById: {},
  reportsById: {},
  dashboardsById: {},
  traversalsById: {},
  previousInput: null,
  assetIdToFolderIdLookup: {},
  permissionContext: permissionsOperations.createEmptyPermissionContext(),
  bookmarksById: {},
  viewpointsById: {},
  broadcastsById: {},
};

const mapAssetIdsToFolderIds = (
  assetFoldersById: Record<string, AssetFolder>
) =>
  Object.values(assetFoldersById).reduce<Record<string, ArdoqId>>(
    (acc, { _id: folderId, content }) => {
      content.forEach(({ _id }) => {
        acc[_id] = folderId;
      });
      return acc;
    },
    {}
  );

type GetAssetByIdBaseArgs = {
  isFavorite: (id: string) => boolean;
  getFolderId: (assetId: string) => string | null;
  getEditorImage: (userId: ArdoqId | undefined) => string | undefined;
};

type GetAssetFoldersByIdArgs = GetAssetByIdBaseArgs & {
  assetFoldersById: Record<string, AssetFolder>;
  currentUserIsOrgWriter: boolean;
};
const getAssetFoldersById = ({
  assetFoldersById,
  isFavorite,
  getFolderId,
  getEditorImage,
  currentUserIsOrgWriter,
}: GetAssetFoldersByIdArgs) =>
  enhanceWithMeta<AssetFolder, AssetType.FOLDER>(
    assetFoldersById,
    AssetType.FOLDER,
    ({ _id, lastModifiedBy }) => ({
      favorite: isFavorite(_id),
      folderId: getFolderId(_id) || null,
      lastModifiedByImage: getEditorImage(lastModifiedBy),
      permissions: {
        canUpdate: currentUserIsOrgWriter,
        canDelete: currentUserIsOrgWriter,
      },
    })
  );

type GetWorkspaceAssetsByIdArgs = GetAssetByIdBaseArgs & {
  workspacesById: Record<string, APIWorkspaceOverview>;
  permissionContext: PermissionContext;
  workspaceFieldAccessibility: WorkspaceFieldAccessibilityMap;
  externallyManagedWorkspaceIds: Set<ArdoqId>;
};
// Alias deep* fields onto the shallow names that shared asset columns read
// via `dataIndex: 'lastUpdated' / 'lastModifiedByName'`, so workspace rows
// surface their deep timestamp without a workspace-specific column variant.
const aliasDeepFields = (workspace: APIWorkspaceOverview) => ({
  lastUpdated: workspace.deepLastUpdated,
  lastModifiedBy: workspace.deepLastModifiedBy,
  lastModifiedByName: workspace.deepLastModifiedByName,
});
export const getWorkspaceAssetsById = ({
  workspacesById,
  permissionContext,
  isFavorite,
  getFolderId,
  getEditorImage,
  workspaceFieldAccessibility,
  externallyManagedWorkspaceIds,
}: GetWorkspaceAssetsByIdArgs): Record<
  string,
  WorkspaceAsset & { meta: ClientAssetMeta }
> =>
  enhanceWithMeta<APIWorkspaceOverview, AssetType.WORKSPACE>(
    workspacesById,
    AssetType.WORKSPACE,
    ({ _id, deepLastModifiedBy }) => {
      const canEditWorkspace = workspaceAccessControlInterface.canEditWorkspace(
        permissionContext,
        _id,
        null
      );
      const canAdminWorkspace =
        workspaceAccessControlInterface.canAdminWorkspace(
          permissionContext,
          _id,
          null
        );
      return {
        favorite: isFavorite(_id),
        folderId: getFolderId(_id),
        isExternallyManaged: externallyManagedWorkspaceIds.has(_id),
        lastModifiedByImage: getEditorImage(deepLastModifiedBy),
        permissions: {
          canCreate:
            workspaceAccessControlInterface.canCreateWorkspace(
              permissionContext
            ),
          canRead: workspaceAccessControlInterface.canAccessWorkspace(
            permissionContext,
            _id
          ),
          canCopy: workspaceAccessControlInterface.canCopyWorkspace(
            permissionContext,
            workspaceFieldAccessibility,
            _id
          ),
          canUpdate: canEditWorkspace,
          canAdmin: canAdminWorkspace,
          canDelete: canAdminWorkspace,
          canManageProperties: canEditWorkspace,
          canManagePermissions: canAdminWorkspace,
        },
      };
    },
    aliasDeepFields
  ) as Record<string, WorkspaceAsset>;

type GetPresentationsByIdArgs = GetAssetByIdBaseArgs & {
  presentationsById: Record<string, APIPresentationAssetAttributes>;
  permissionContext: PermissionContext;
};

export const getPresentationsById = ({
  presentationsById,
  isFavorite,
  getFolderId,
  getEditorImage,
  permissionContext,
}: GetPresentationsByIdArgs) =>
  enhanceWithMeta<APIPresentationAssetAttributes, AssetType.PRESENTATION>(
    presentationsById,
    AssetType.PRESENTATION,
    presentation => {
      const canEditPresentation =
        presentationPermissionOperations.canEditPresentation(
          permissionContext,
          presentation
        );
      const canCopyPresentation =
        presentationPermissionOperations.canCopyPresentation(
          permissionContext,
          presentation
        );
      return {
        favorite: isFavorite(presentation._id),
        folderId: getFolderId(presentation._id),
        lastModifiedByImage: getEditorImage(presentation.lastModifiedBy),
        permissions: {
          canUpdate: canEditPresentation,
          canDelete: canEditPresentation,
          canCopy: canCopyPresentation,
        },
      };
    }
  );

type GetBookmarksByIdArgs = GetAssetByIdBaseArgs & {
  bookmarksById: Record<string, APIBookmarkAttributes>;
  currentUserIsOrgWriter: boolean;
  currentUserIsOrgAdmin: boolean;
};
const getBookmarksById = ({
  bookmarksById,
  isFavorite,
  getFolderId,
  getEditorImage,
  currentUserIsOrgWriter,
  currentUserIsOrgAdmin,
}: GetBookmarksByIdArgs) =>
  enhanceWithMeta<APIBookmarkAttributes, AssetType.BOOKMARK>(
    bookmarksById,
    AssetType.BOOKMARK,
    ({ _id, lastModifiedBy }) => {
      return {
        favorite: isFavorite(_id),
        folderId: getFolderId(_id),
        lastModifiedByImage: getEditorImage(lastModifiedBy),
        permissions: {
          canManagePermissions: false, // bookmarks don't have this feature implemented
          canUpdate: true, // TODO AM bookmarks fix permissions
          canDelete: true, // TODO AM bookmarks fix permissions
          canCopy: currentUserIsOrgWriter || currentUserIsOrgAdmin, // TODO AM bookmarks fix permissions
        },
      };
    }
  );

type GetSurveysByIdArgs = GetAssetByIdBaseArgs & {
  surveysById: Record<string, APISurveyAttributes>;
  canAdminSurvey: (id: string) => boolean;
  canEditSurvey: (id: string) => boolean;
};
const getSurveysById = ({
  surveysById,
  isFavorite,
  getFolderId,
  getEditorImage,
  canAdminSurvey,
  canEditSurvey,
}: GetSurveysByIdArgs) =>
  pickBy(
    enhanceWithMeta<APISurveyAttributes, AssetType.SURVEY>(
      surveysById,
      AssetType.SURVEY,
      ({ _id, lastModifiedBy }) => ({
        favorite: isFavorite(_id),
        folderId: getFolderId(_id),
        lastModifiedByImage: getEditorImage(lastModifiedBy),
        permissions: {
          canManagePermissions: canAdminSurvey(_id),
          canUpdate: canEditSurvey(_id),
          canDelete: canEditSurvey(_id),
        },
      })
    ),
    survey => survey.published || survey.meta.permissions.canUpdate
  );

type GetBroadcastsByIdArgs = GetAssetByIdBaseArgs & {
  broadcastsById: Record<string, APIBroadcastAttributes>;
  currentUserIsOrgWriter: boolean;
  currentUserIsOrgAdmin: boolean;
};
const getBroadcastsById = ({
  broadcastsById,
  isFavorite,
  getFolderId,
  getEditorImage,
  currentUserIsOrgWriter,
  currentUserIsOrgAdmin,
}: GetBroadcastsByIdArgs) =>
  enhanceWithMeta<APIBroadcastAttributes, AssetType.BROADCAST>(
    broadcastsById,
    AssetType.BROADCAST,
    broadcast => ({
      favorite: isFavorite(broadcast._id),
      folderId: getFolderId(broadcast._id),
      lastModifiedByImage: getEditorImage(broadcast.lastModifiedBy),
      permissions: {
        canUpdate: currentUserIsOrgWriter,
        canDelete: currentUserIsOrgWriter,
        canCopy: currentUserIsOrgWriter || currentUserIsOrgAdmin,
      },
      latestSchedule: getLatestSchedule(broadcast),
      intervalInDays: getIntervalInDays(broadcast.scheduling),
    })
  );

type GetMetaModelsByIdArgs = GetAssetByIdBaseArgs & {
  metamodelsById: Record<string, PersistedMetamodel>;
  currentUserIsOrgWriter: boolean;
  currentUserIsOrgAdmin: boolean;
};
const getMetamodelsById = ({
  metamodelsById,
  isFavorite,
  getFolderId,
  getEditorImage,
  currentUserIsOrgWriter,
  currentUserIsOrgAdmin,
}: GetMetaModelsByIdArgs) =>
  enhanceWithMeta<PersistedMetamodel, AssetType.METAMODEL>(
    metamodelsById,
    AssetType.METAMODEL,
    ({ _id, wholeOrg, lastModifiedBy }) => ({
      favorite: isFavorite(_id),
      folderId: getFolderId(_id),
      lastModifiedByImage: getEditorImage(lastModifiedBy),
      permissions: {
        canUpdate: currentUserIsOrgWriter,
        canDelete: !wholeOrg && currentUserIsOrgWriter,
        canCopy: currentUserIsOrgWriter || currentUserIsOrgAdmin,
      },
    })
  );
type GetScenariosByIdArgs = GetAssetByIdBaseArgs & {
  scenariosById: Record<string, APIScenarioAttributes>;
  permissionContext: PermissionContext;
};
const getScenariosById = ({
  scenariosById,
  isFavorite,
  getFolderId,
  getEditorImage,
  permissionContext,
}: GetScenariosByIdArgs) =>
  enhanceWithMeta<APIScenarioAttributes, AssetType.SCENARIO>(
    scenariosById,
    AssetType.SCENARIO,
    ({ _id, lastModifiedBy }) => ({
      favorite: isFavorite(_id),
      folderId: getFolderId(_id),
      lastModifiedByImage: getEditorImage(lastModifiedBy),
      permissions: {
        canManagePermissions: scenarioAccessControlInterface.canAdminScenario(
          permissionContext,
          _id
        ),
        canUpdate: scenarioAccessControlInterface.canEditScenario(
          permissionContext,
          _id
        ),
        canDelete: scenarioAccessControlInterface.canAdminScenario(
          permissionContext,
          _id
        ),
      },
    })
  );

type GetReportsByIdArgs = GetAssetByIdBaseArgs & {
  reportsById: Record<ArdoqId, APIReportAttributes>;
  permissionContext: PermissionContext;
  currentUserIsOrgAdmin: boolean;
  currentUserIsOrgWriter: boolean;
  workspaceFieldAccessibility: WorkspaceFieldAccessibilityMap;
};
export const getReportsById = ({
  reportsById,
  permissionContext,
  isFavorite,
  getFolderId,
  getEditorImage,
  currentUserIsOrgAdmin,
  currentUserIsOrgWriter,
  workspaceFieldAccessibility,
}: GetReportsByIdArgs) =>
  enhanceWithMeta<APIReportAttributes, AssetType.REPORT>(
    reportsById,
    AssetType.REPORT,
    report => ({
      favorite: isFavorite(report._id),
      folderId: getFolderId(report._id),
      lastModifiedByImage: getEditorImage(report.lastModifiedBy),
      permissions: {
        canManagePermissions: hasResourcePermission(
          Permissions.REPORTS_ADMIN,
          permissionContext,
          report._id
        ),
        canUpdate: hasResourcePermission(
          Permissions.REPORTS_EDIT,
          permissionContext,
          report._id
        ),
        canDelete: hasResourcePermission(
          Permissions.REPORTS_ADMIN,
          permissionContext,
          report._id
        ),
        canCopy:
          currentUserIsOrgAdmin ||
          (currentUserIsOrgWriter &&
            hasResourcePermission(
              Permissions.REPORTS_READ,
              permissionContext,
              report._id
            )),
        hasInaccessibleConfidentialFields:
          reportHasInaccessibleConfidentialFields(
            workspaceFieldAccessibility,
            report
          ),
      },
    })
  );

type GetDashboardsByIdArgs = GetAssetByIdBaseArgs & {
  dashboardsById: Record<string, APIDashboardAttributes>;
  permissionContext: PermissionContext;
  reportsById: Record<ArdoqId, APIReportAttributes>;
  workspaceFieldAccessibility: WorkspaceFieldAccessibilityMap;
};
export const getDashboardsById = ({
  dashboardsById,
  isFavorite,
  getFolderId,
  getEditorImage,
  permissionContext,
  reportsById,
  workspaceFieldAccessibility,
}: GetDashboardsByIdArgs) =>
  enhanceWithMeta<APIDashboardAttributes, AssetType.DASHBOARD>(
    dashboardsById,
    AssetType.DASHBOARD,
    dashboard => ({
      favorite: isFavorite(dashboard._id),
      folderId: getFolderId(dashboard._id),
      lastModifiedByImage: getEditorImage(dashboard.lastModifiedBy),
      permissions: {
        // TODO
        canManagePermissions: dashboardAccessControlInterface.canAdminDashboard(
          permissionContext,
          dashboard._id
        ),
        canUpdate: dashboardAccessControlInterface.canEditDashboard(
          permissionContext,
          dashboard._id
        ),
        canRead: dashboardAccessControlInterface.canReadDashboard(
          permissionContext,
          dashboard._id
        ),
        canDelete: dashboardAccessControlInterface.canAdminDashboard(
          permissionContext,
          dashboard._id
        ),
        canCopy:
          dashboardAccessControlInterface.canCopyDashboard(permissionContext),
        hasInaccessibleConfidentialFields:
          dashboardHasInaccessibleConfidentialFields(
            workspaceFieldAccessibility,
            reportsById,
            dashboard
          ),
      },
    })
  );

type GetTraversalsByIdArgs = GetAssetByIdBaseArgs & {
  traversalsById: Record<string, APIViewpointAttributes>;
  permissionContext: PermissionContext;
};
export const getTraversalsById = ({
  traversalsById,
  isFavorite,
  getFolderId,
  getEditorImage,
  permissionContext,
}: GetTraversalsByIdArgs) =>
  enhanceWithMeta<APIViewpointAttributes, AssetType.TRAVERSAL>(
    traversalsById,
    AssetType.TRAVERSAL,
    ({ _id, lastModifiedBy }) => {
      const canCurrentUserEditTraversal =
        resourcePermissionsOperations.canEditTraversal(permissionContext, _id);

      return {
        favorite: isFavorite(_id),
        folderId: getFolderId(_id),
        lastModifiedByImage: getEditorImage(lastModifiedBy),
        permissions: {
          canManagePermissions: resourcePermissionsOperations.canAdminTraversal(
            permissionContext,
            _id
          ),
          canUpdate: canCurrentUserEditTraversal,
          canDelete: canCurrentUserEditTraversal,
          canCopy:
            resourcePermissionsOperations.canCreateTraversal(permissionContext),
        },
      };
    }
  );

type GetViewpointsByIdArgs = GetAssetByIdBaseArgs & {
  viewpointsById: Record<string, APIDiscoverViewpointAttributes>;
  permissionContext: PermissionContext;
};
const getViewpointsById = ({
  viewpointsById,
  isFavorite,
  getFolderId,
  getEditorImage,
  permissionContext,
}: GetViewpointsByIdArgs) =>
  enhanceWithMeta<APIDiscoverViewpointAttributes, AssetType.VIEWPOINT>(
    viewpointsById,
    AssetType.VIEWPOINT,
    ({ _id, lastModifiedBy }) => ({
      favorite: isFavorite(_id),
      folderId: getFolderId(_id),
      lastModifiedByImage: getEditorImage(lastModifiedBy),
      permissions: {
        canManagePermissions: viewpointAccessControlInterface.canAdminViewpoint(
          permissionContext,
          _id
        ),
        canUpdate: viewpointAccessControlInterface.canEditViewpoint(
          permissionContext,
          _id
        ),
        canDelete: viewpointAccessControlInterface.canEditViewpoint(
          permissionContext,
          _id
        ),
        canCopy:
          viewpointAccessControlInterface.canCopyViewpoint(permissionContext),
      },
    })
  );

const isStateUpdated =
  (previousState: AssetsStreamState) =>
  (streamPart: unknown, streamIndex: number) =>
    !previousState.previousInput ||
    streamPart !== previousState.previousInput[streamIndex];

type AssetsUpdatedState = {
  currentUserStateUpdated: boolean;
  assetFoldersStateUpdated: boolean;
  workspacesStateUpdated: boolean;
  presentationsStateUpdated: boolean;
  surveysStateUpdated: boolean;
  metamodelsStateUpdated: boolean;
  scenariosStateUpdated: boolean;
  reportsStateUpdated: boolean;
  dashboardsStateUpdated: boolean;
  traversalsStateUpdated: boolean;
  currentUserPermissionStateUpdated: boolean;
  bookmarksStateUpdated: boolean;
  viewpointsStateUpdated: boolean;
  broadcastsStateUpdated: boolean;
  fieldAccessibilityStateUpdated: boolean;
  featureContextStateUpdated: boolean;
  externallyManagedStateUpdated: boolean;
  orgUsersStateUpdated: boolean;
};
const assetsToRefresh = ({
  currentUserStateUpdated,
  assetFoldersStateUpdated,
  workspacesStateUpdated,
  presentationsStateUpdated,
  surveysStateUpdated,
  metamodelsStateUpdated,
  scenariosStateUpdated,
  reportsStateUpdated,
  dashboardsStateUpdated,
  traversalsStateUpdated,
  currentUserPermissionStateUpdated,
  bookmarksStateUpdated,
  viewpointsStateUpdated,
  broadcastsStateUpdated,
  fieldAccessibilityStateUpdated,
  featureContextStateUpdated,
  externallyManagedStateUpdated,
  orgUsersStateUpdated,
}: AssetsUpdatedState) => ({
  refreshAssetFoldersById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated,
  refreshWorkspaceAssetsById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    workspacesStateUpdated ||
    fieldAccessibilityStateUpdated ||
    externallyManagedStateUpdated,
  refreshPresentationsById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    presentationsStateUpdated,
  refreshSurveysById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    surveysStateUpdated ||
    featureContextStateUpdated,
  refreshMetamodelsById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    metamodelsStateUpdated,
  refreshScenariosById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    scenariosStateUpdated,
  refreshReportsById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    reportsStateUpdated ||
    fieldAccessibilityStateUpdated,
  refreshDashboardsById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    dashboardsStateUpdated ||
    reportsStateUpdated ||
    fieldAccessibilityStateUpdated,
  refreshTraversalsById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    traversalsStateUpdated,
  refreshBookmarksById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    bookmarksStateUpdated,
  refreshViewpointsById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    viewpointsStateUpdated,
  refreshBroadcastsById:
    currentUserPermissionStateUpdated ||
    assetFoldersStateUpdated ||
    currentUserStateUpdated ||
    orgUsersStateUpdated ||
    broadcastsStateUpdated ||
    featureContextStateUpdated,
});

const mapProps = (
  previousState: AssetsStreamState,
  inputStream: InputStreamShape
): AssetsStreamState => {
  const [
    currentUserData,
    assetFoldersByIdWithoutMeta,
    workspacesByIdWithoutMeta,
    presentationsByIdWithoutMeta,
    surveysByIdWithoutMeta,
    metamodelsByIdWithoutMeta,
    scenariosByIdWithoutMeta,
    reportsByIdWithoutMeta,
    dashboardsByIdWithoutMeta,
    traversalsByIdWithoutMeta,
    permissionContext,
    bookmarksByIdWithoutMeta,
    viewpointsByIdWithoutMeta,
    broadcastsByIdWithoutMeta,
    workspaceFieldAccessibility,
    featureContext,
    externallyManagedWorkspaceIdsList,
    userImagesById,
  ] = inputStream;
  const externallyManagedWorkspaceIds = new Set(
    externallyManagedWorkspaceIdsList
  );
  const getEditorImage = (userId: ArdoqId | undefined) =>
    userId ? userImagesById[userId] : undefined;

  const [
    currentUserStateUpdated,
    assetFoldersStateUpdated,
    workspacesStateUpdated,
    presentationsStateUpdated,
    surveysStateUpdated,
    metamodelsStateUpdated,
    scenariosStateUpdated,
    reportsStateUpdated,
    dashboardsStateUpdated,
    traversalsStateUpdated,
    currentUserPermissionStateUpdated,
    bookmarksStateUpdated,
    viewpointsStateUpdated,
    broadcastsStateUpdated,
    fieldAccessibilityStateUpdated,
    featureContextStateUpdated,
    externallyManagedStateUpdated,
    orgUsersStateUpdated,
  ] = inputStream.map(isStateUpdated(previousState));
  const surveysEnabled = hasFeature(Features.SURVEYS);
  const canAdminBroadcasts =
    broadcastAccessControlInterface.canAdminBroadcasts(featureContext);

  const {
    isOrgWriter: currentUserIsOrgWriter,
    isOrgAdmin: currentUserIsOrgAdmin,
    favorites,
  } = currentUserData;

  const assetIdToFolderIdLookup = assetFoldersStateUpdated
    ? mapAssetIdsToFolderIds(assetFoldersByIdWithoutMeta)
    : previousState.assetIdToFolderIdLookup;

  const {
    refreshAssetFoldersById,
    refreshWorkspaceAssetsById,
    refreshPresentationsById,
    refreshSurveysById,
    refreshMetamodelsById,
    refreshScenariosById,
    refreshReportsById,
    refreshDashboardsById,
    refreshTraversalsById,
    refreshBookmarksById,
    refreshViewpointsById,
    refreshBroadcastsById,
  } = assetsToRefresh({
    currentUserStateUpdated,
    assetFoldersStateUpdated,
    workspacesStateUpdated,
    presentationsStateUpdated,
    surveysStateUpdated,
    metamodelsStateUpdated,
    scenariosStateUpdated,
    reportsStateUpdated,
    dashboardsStateUpdated,
    traversalsStateUpdated,
    currentUserPermissionStateUpdated,
    bookmarksStateUpdated,
    viewpointsStateUpdated,
    broadcastsStateUpdated,
    fieldAccessibilityStateUpdated,
    featureContextStateUpdated,
    externallyManagedStateUpdated,
    orgUsersStateUpdated,
  });

  const getFolderId = (id: string) => assetIdToFolderIdLookup[id] || null;
  const assetFoldersById = refreshAssetFoldersById
    ? getAssetFoldersById({
        assetFoldersById: assetFoldersByIdWithoutMeta,
        isFavorite: id => favorites[AssetType.FOLDER].includes(id),
        getFolderId,
        getEditorImage,
        currentUserIsOrgWriter,
      })
    : previousState.assetFoldersById;

  const workspaceAssetsById = refreshWorkspaceAssetsById
    ? getWorkspaceAssetsById({
        workspacesById: workspacesByIdWithoutMeta,
        permissionContext,
        workspaceFieldAccessibility,
        isFavorite: id => favorites[AssetType.WORKSPACE].includes(id),
        getFolderId,
        getEditorImage,
        externallyManagedWorkspaceIds,
      })
    : previousState.workspaceAssetsById;

  const presentationsById = refreshPresentationsById
    ? getPresentationsById({
        presentationsById: presentationsByIdWithoutMeta,
        isFavorite: id => favorites[AssetType.PRESENTATION].includes(id),
        getFolderId,
        getEditorImage,
        permissionContext,
      })
    : previousState.presentationsById;

  const { canAdminSurvey, canEditSurvey } = surveyAccessControlInterface;
  const surveysById = refreshSurveysById
    ? surveysEnabled
      ? getSurveysById({
          surveysById: surveysByIdWithoutMeta,
          isFavorite: id => favorites[AssetType.SURVEY].includes(id),
          getFolderId,
          getEditorImage,
          canAdminSurvey: id => canAdminSurvey(featureContext, id),
          canEditSurvey: id => canEditSurvey(featureContext, id),
        })
      : {}
    : previousState.surveysById;

  const metamodelsById = refreshMetamodelsById
    ? getMetamodelsById({
        metamodelsById: metamodelsByIdWithoutMeta,
        isFavorite: id => favorites[AssetType.METAMODEL].includes(id),
        getFolderId,
        getEditorImage,
        currentUserIsOrgWriter,
        currentUserIsOrgAdmin,
      })
    : previousState.metamodelsById;

  const scenariosById = refreshScenariosById
    ? getScenariosById({
        scenariosById: scenariosByIdWithoutMeta,
        isFavorite: id => favorites[AssetType.SCENARIO].includes(id),
        getFolderId,
        getEditorImage,
        permissionContext,
      })
    : previousState.scenariosById;

  const reportsById = refreshReportsById
    ? getReportsById({
        reportsById: reportsByIdWithoutMeta,
        permissionContext,
        isFavorite: id => favorites[AssetType.REPORT].includes(id),
        getFolderId,
        getEditorImage,
        currentUserIsOrgAdmin,
        currentUserIsOrgWriter,
        workspaceFieldAccessibility,
      })
    : previousState.reportsById;

  const dashboardsById = refreshDashboardsById
    ? getDashboardsById({
        dashboardsById: dashboardsByIdWithoutMeta,
        isFavorite: id => favorites[AssetType.DASHBOARD].includes(id),
        getFolderId,
        getEditorImage,
        permissionContext,
        reportsById: reportsByIdWithoutMeta,
        workspaceFieldAccessibility,
      })
    : previousState.dashboardsById;

  const traversalsById = refreshTraversalsById
    ? getTraversalsById({
        traversalsById: traversalsByIdWithoutMeta,
        isFavorite: id => favorites[AssetType.TRAVERSAL].includes(id),
        getFolderId,
        getEditorImage,
        permissionContext,
      })
    : previousState.traversalsById;

  const bookmarksById = refreshBookmarksById
    ? getBookmarksById({
        bookmarksById: bookmarksByIdWithoutMeta,
        isFavorite: id => favorites[AssetType.BOOKMARK].includes(id),
        getFolderId,
        getEditorImage,
        currentUserIsOrgAdmin,
        currentUserIsOrgWriter,
      })
    : previousState.bookmarksById;

  const shouldHideDiscoverViewpoints =
    viewpointAccessControlInterface.getDiscoverViewpointOverViewHidden(
      permissionContext,
      featureContext,
      Object.values(viewpointsByIdWithoutMeta)
    );

  const viewpointsById = shouldHideDiscoverViewpoints
    ? {}
    : refreshViewpointsById
      ? getViewpointsById({
          viewpointsById: viewpointsByIdWithoutMeta,
          isFavorite: id => favorites[AssetType.VIEWPOINT].includes(id),
          getFolderId,
          getEditorImage,
          permissionContext,
        })
      : previousState.viewpointsById;

  const broadcastsById = refreshBroadcastsById
    ? canAdminBroadcasts
      ? getBroadcastsById({
          broadcastsById: broadcastsByIdWithoutMeta,
          isFavorite: id => favorites[AssetType.BROADCAST].includes(id),
          getFolderId,
          getEditorImage,
          currentUserIsOrgAdmin,
          currentUserIsOrgWriter,
        })
      : {}
    : previousState.broadcastsById;

  const result = {
    assetFoldersById,
    workspaceAssetsById,
    presentationsById,
    surveysById,
    scenariosById,
    metamodelsById,
    reportsById,
    dashboardsById,
    traversalsById,
    bookmarksById,
    viewpointsById,
    broadcastsById,
    previousInput: inputStream,
    assetIdToFolderIdLookup,
    permissionContext,
  };
  return result;
};

const toCurrentUserAssetData = (
  user: APICurrentUser
): CurrentUserAssetData => ({
  isOrgWriter: userOperations.hasWriterRolePlus(user),
  isOrgAdmin: userOperations.hasAdminRole(user),
  favorites: getFavorites(user),
});

export const toExternallyManagedWorkspaceIds = ({
  models,
}: WorkspacesState): ArdoqId[] =>
  models
    .filter(workspace => (workspace.integrations?.length ?? 0) > 0)
    .map(({ _id }) => _id);

export const toUserImagesById = ({
  byId,
}: OrgUsersStreamShape): Record<ArdoqId, string | undefined> =>
  mapValues(byId, user => user.linkedComponent?.image ?? undefined);

const assets$ = derivedStream(
  'asset$',
  currentUser$.pipe(
    map(toCurrentUserAssetData),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  assetFolders$.pipe(
    distinctUntilChanged((a, b) => isEqual(a, b)),
    map(folders => keyBy(folders, '_id'))
  ),
  workspaces$.pipe(
    map(({ byId }) => byId),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  presentations$.pipe(
    map(({ byId }) => byId),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  surveys$.pipe(
    map(({ byId }) => byId),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  metamodels$.pipe(map(toByIdDictionary), distinctUntilChanged()),
  scenarios$.pipe(
    map(({ byId }) => byId),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  reports$.pipe(
    map(({ byId }) => byId),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  dashboards$.pipe(
    map(({ dashboardsById }) => dashboardsById),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  traversals$.pipe(
    map(traversalStateOperations.getAllById),
    distinctUntilChanged()
  ),
  currentUserPermissionContext$.pipe(
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  bookmarks$.pipe(
    map(({ byId }) => byId),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  viewpoints$.pipe(
    map(({ viewpoints }) => (viewpoints ? toByIdDictionary(viewpoints) : {})),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  broadcast$.pipe(
    map(({ broadcasts }) => toByIdDictionary(broadcasts)),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  workspaceFieldAccessibility$,
  featureContext$,
  persistentWorkspaces$.pipe(
    map(toExternallyManagedWorkspaceIds),
    distinctUntilChanged((a, b) => isEqual(a, b))
  ),
  // Performance: `byId` is replaced wholesale on every `fetchedOrgUsersSuccess`,
  // and `fetchOrgUsers` fires from many places while the assets browser is
  // mounted. Comparing the projected images structurally keeps an identical
  // refetch from rebuilding all thirteen asset collections.
  orgUsers$.pipe(
    map(toUserImagesById),
    distinctUntilChanged((a, b) => isEqual(a, b))
  )
).pipe(
  scan(
    (previousState: AssetsStreamState, inputStream: unknown) =>
      mapProps(previousState, inputStream as InputStreamShape), // TODO AM remove casting once PR https://github.com/ardoq/rxbeach/pull/674 is merged
    emptyState
  ),
  shareReplay({ refCount: true, bufferSize: 1 })
);

export { assets$ };
